import React, { useState, useLayoutEffect } from 'react';
import { Swipeable, SwipeCallback } from 'react-swipeable';
import styles from './ImageCarousel.module.scss';
import Div from '../Element/Div/Div';
import BackgroundImage from '../BackgroundImage/BackgroundImage';
import Flex from '../Flex/Flex';
import Icon from '../Icon/Icon';
import Gutter from '../Gutter/Gutter';
import { CreateProps } from '../../types/utils/CreateProps';
import { SupportedColor } from '../Element/Element';
import SelectionButton from '../SelectionButton/SelectionButton';

type MainImageProps = CreateProps<
  {
    src: string;
    alt: string;
    onSwipedRight: SwipeCallback;
    onSwipedLeft: SwipeCallback;
  },
  typeof Div
>;

const MainImage: React.FunctionComponent<MainImageProps> = ({
  src,
  alt,
  onSwipedRight,
  onSwipedLeft,
}) => {
  return (
    <Flex centeredVertically>
      <Div className={styles.imageCarousel__mainImage}>
        <Div className={styles.imageCarousel__mainImage__wrapper}>
          <Swipeable
            className={styles.imageCarousel__mainImage__imageContainer}
            onSwipedRight={onSwipedRight}
            onSwipedLeft={onSwipedLeft}
            trackMouse
          >
            <BackgroundImage
              src={src}
              alt={alt}
              className={styles.imageCarousel__mainImage__image}
              scaleWithOriginalAspectRatio
            />
          </Swipeable>
        </Div>
      </Div>
    </Flex>
  );
};

export interface ImageProps {
  url: string;
  name: string;
}
// Alias for backwards compatibility
export type ImageCarouselImageShape = ImageProps;

type ImageCarouselProps = CreateProps<
  {
    /** Array of image objects (image url and name to be used on alt property) that are to be shown. */
    images: ImageProps[];
    /** Number of thumbnails shown at a time. */
    numberShown?: number;
  },
  typeof Div,
  'padding'
>;

const ImageCarousel: React.FunctionComponent<ImageCarouselProps> = ({
  images,
  numberShown = 4,
  ...props
}) => {
  const [currentImg, setCurrentImg] = useState(0);
  const [currentSet, setCurrentSet] = useState(0);
  let setMaxIdx = Math.floor((images.length - 1) / numberShown);

  useLayoutEffect(() => {
    setCurrentImg(0);
    setCurrentSet(0);
    setMaxIdx = Math.floor((images.length - 1) / numberShown);
  }, [images]);

  const isScrollableToLeft = currentSet - 1 < 0;
  const isScrollableToRight = currentSet + 1 > setMaxIdx;

  const leftIconStyles = {
    color: isScrollableToLeft
      ? ('gray300' as SupportedColor)
      : ('black' as SupportedColor),
    hoverColor: isScrollableToLeft
      ? undefined
      : ('purple500' as SupportedColor),
  };
  const rightIconStyles = {
    color: isScrollableToRight
      ? ('gray300' as SupportedColor)
      : ('black' as SupportedColor),
    hoverColor: isScrollableToRight
      ? undefined
      : ('purple500' as SupportedColor),
  };

  const nextImage = () => {
    const next = currentImg + 1;
    if (next > images.length - 1) return;
    setCurrentImg(next);

    /* Function which automatically rolls thumbnails right when user swipes */
    /* istanbul ignore if */
    if ((next / numberShown) % 1 === 0) {
      carouselRollRight();
    }
  };

  const prevImage = () => {
    const next = currentImg - 1;
    if (next < 0) return;
    setCurrentImg(next);

    /* Function which automatically rolls thumbnails left when user swipes */
    /* istanbul ignore if */
    if (((next + 1) / numberShown) % 1 === 0) {
      carouselRollLeft();
    }
  };

  const carouselRollRight = () => {
    const next = currentSet + 1;
    if (next > setMaxIdx) return;
    setCurrentSet(next);
  };

  const carouselRollLeft = () => {
    const next = currentSet - 1;
    if (next < 0) return;
    setCurrentSet(next);
  };

  const solveImageUrl =
    currentImg >= images.length ? '' : images[currentImg].url;
  const solveImageAlt =
    currentImg >= images.length ? '' : images[currentImg].name;
  return (
    <Div padding={{ size: 'lg' }} {...props}>
      <MainImage
        src={solveImageUrl}
        alt={solveImageAlt}
        onSwipedLeft={nextImage}
        onSwipedRight={prevImage}
      />
      <Gutter size="lg" />
      <Swipeable
        onSwipedRight={carouselRollLeft}
        onSwipedLeft={carouselRollRight}
        trackMouse
      >
        <Flex centered>
          {setMaxIdx > 0 && (
            <Icon
              margin={{ right: 'xxs' }}
              name="chevron-left"
              size="sm"
              clickableAppearance
              onClick={carouselRollLeft}
              {...leftIconStyles}
            />
          )}

          {/* Uses responsiveHidden, make sure they match so we dont render both mobile and other bottom bar */}
          {/* Desktop, tablets... */}
          {images
            .slice(currentSet * numberShown, (currentSet + 1) * numberShown)
            .map((image, i) => (
              <SelectionButton
                id={image.name}
                key={currentSet * numberShown + i}
                src={image.url}
                alt={image.name}
                selected={currentSet * numberShown + i === currentImg}
                onClick={() => setCurrentImg(currentSet * numberShown + i)}
                responsiveHidden={{ thinnerThan: 'sm' }}
              />
            ))}
          {/* Small screen phones. We no longer use thumbnails, we use highlighted dot. Number of dots equals to total number of images */}
          {images.map((_, i) => (
            <Div
              key={i}
              className={[
                styles.imageCarousel__thumbnail__small,
                i === currentImg
                  ? styles.imageCarousel__thumbnail__small__active
                  : '',
              ].join(' ')}
              responsiveHidden={{ widerThan: 'xs' }}
            />
          ))}
          {setMaxIdx > 0 && (
            <Icon
              name="chevron-right"
              size="sm"
              onClick={carouselRollRight}
              {...rightIconStyles}
              clickableAppearance
            />
          )}
        </Flex>
      </Swipeable>
    </Div>
  );
};

export default ImageCarousel;
